import useGetLocations from '@/hooks/locations/useGetLocations.tsx'
import { Button } from '@/shadcn/components/ui/button.tsx'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/shadcn/components/ui/select.tsx'
import { Skeleton } from '@/shadcn/components/ui/skeleton.tsx'
import { Undo2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LocationEntity } from '../../../qourses-api-client'

export function LocationSelection(props: {
  setSelectedLocation: (value: LocationEntity) => void
  initiallySelectedLocationId?: string
  disabled?: boolean
  setChanged?: (value: boolean) => void
  isRefetching?: boolean
  hideUndo?: boolean
}) {
  const { t: translate } = useTranslation()
  const { locations, isLoading, isError, isEmpty, refetch, isRefetching } = useGetLocations()

  const [locationJson, setLocationJson] = useState<string | null>(null)
  const [key, setKey] = useState<number>(+new Date())

  // If the parent refetches we want to do the same
  useEffect(() => {
    if (props.isRefetching) {
      refetch()
      props.setChanged?.(false)
    }
  }, [props.isRefetching])

  // Set the initially selected location if it is defined
  useEffect(() => {
    if (!isLoading && props.initiallySelectedLocationId !== undefined) {
      const location = locations.find(
        (location) => location.id === props.initiallySelectedLocationId,
      )
      if (location) {
        setLocationJson(JSON.stringify(location))
      }
    }
  }, [isLoading, props.initiallySelectedLocationId, isRefetching])

  useEffect(() => {
    if (locationJson) {
      const location = JSON.parse(locationJson) as LocationEntity
      props.setSelectedLocation(location)
      //This is for internal use only so we can access whatever is currently selected in the select component
      if (props.setChanged) {
        if (location.id !== props.initiallySelectedLocationId) {
          props.setChanged(true)
        } else {
          props.setChanged(false)
        }
      }
    } else {
      props.setSelectedLocation(null)
      if (props.setChanged) {
        if (props.initiallySelectedLocationId !== null) {
          props.setChanged(true)
        } else {
          props.setChanged(false)
        }
      }
    }
  }, [locationJson])

  if (isEmpty) {
    return (
      <Select disabled={true}>
        <SelectTrigger className="bg-white">
          <SelectValue placeholder={translate('dropdowns.locations.empty')}></SelectValue>
        </SelectTrigger>
      </Select>
    )
  }

  if (isError) {
    return <div>Error</div>
  }

  return (
    <div className="flex flex-row">
      <Select
        key={key}
        onValueChange={(value) => setLocationJson(value)}
        disabled={props.disabled ?? false}
        value={locationJson ?? undefined}
      >
        <SelectTrigger className="bg-white">
          <SelectValue placeholder={translate('dropdowns.locations.placeholder')}></SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            <SelectLabel>{translate('dropdowns.locations.title')}</SelectLabel>
            {isLoading && (
              <div className="space-y-2">
                <Skeleton className="h-[25px] bg-gray-200" />
                <Skeleton className="h-[25px] bg-gray-200" />
                <Skeleton className="h-[25px] bg-gray-200" />
              </div>
            )}
            {!isLoading &&
              locations.map((location) => (
                <SelectItem value={JSON.stringify(location)}>{location.name}</SelectItem>
              ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      {!props.hideUndo && (
        <Button
          disabled={!locationJson}
          className="ml-1"
          variant="secondary"
          onClick={(e) => {
            e.stopPropagation()
            setLocationJson(undefined)
            // Radix :)
            setKey(+new Date())
          }}
          size="icon"
        >
          <Undo2 className="size-4" />
        </Button>
      )}
    </div>
  )
}
